import { readonly, ref, shallowRef } from 'vue';

type SnackbarType = 'success' | 'error' | 'warning' | 'info';

type Snack = {
  id: number;
  message: string;
  type: SnackbarType;
  timeout: number;
  dismissible: boolean;
  showProgress: boolean;
  icon: string;
};

type SnackbarOptions = Partial<Snack> & {
  message: string;
};

/**
 * Singleton instance of snackbar composable
 * Stored in shallowRef to ensure same instance is returned every time
 */
const snackbarInstance = shallowRef<ReturnType<typeof createSnackbarInstance> | null>(null);

/**
 * Create snackbar composable instance
 * This function is only called once to create the singleton
 * All state and logic is encapsulated within this function
 */
function createSnackbarInstance() {
  const snacks = ref<Snack[]>([]);
  let nextId = 0;

  const getDefaultIcon = (type: SnackbarType): string => {
    const icons: Record<SnackbarType, string> = {
      success: 'mdi mdi-check-circle',
      error: 'mdi mdi-alert-circle',
      warning: 'mdi mdi-alert',
      info: 'mdi mdi-information',
    };
    return icons[type] || icons.info;
  };

  /**
   * Add a snack to the queue
   */
  const addSnack = (options: SnackbarOptions): number => {
    const {
      message = '',
      type = 'info',
      timeout = 5000,
      dismissible = true,
      showProgress = true,
      icon = null,
    } = options;

    const id = nextId++;
    const snack: Snack = {
      id,
      message,
      type,
      timeout,
      dismissible,
      showProgress,
      icon: icon || getDefaultIcon(type),
    };

    snacks.value.push(snack);

    if (timeout > 0) {
      setTimeout(() => {
        removeSnack(id);
      }, timeout);
    }

    return id;
  };

  /**
   * Remove a snack from the queue
   */
  const removeSnack = (id: number): void => {
    const index = snacks.value.findIndex((s) => s.id === id);
    if (index > -1) {
      snacks.value.splice(index, 1);
    }
  };

  /**
   * Show a snackbar with custom options
   * @param options - Snackbar configuration
   * @returns Snackbar ID
   */
  const showSnackbar = (options: SnackbarOptions): number => {
    return addSnack(options);
  };

  /**
   * Show a success snackbar
   * @param message - Success message
   * @param timeout - Duration in ms (default: 3000, 0 for persistent)
   * @returns Snackbar ID
   */
  const showSuccess = (message: string, timeout: number = 3000): number => {
    return showSnackbar({
      message,
      type: 'success',
      timeout,
    });
  };

  /**
   * Show an error snackbar
   * @param message - Error message
   * @param timeout - Duration in ms (default: 5000, 0 for persistent)
   * @returns Snackbar ID
   */
  const showError = (message: string, timeout: number = 5000): number => {
    return showSnackbar({
      message,
      type: 'error',
      timeout,
    });
  };

  /**
   * Show a warning snackbar
   * @param message - Warning message
   * @param timeout - Duration in ms (default: 4000, 0 for persistent)
   * @returns Snackbar ID
   */
  const showWarning = (message: string, timeout: number = 4000): number => {
    return showSnackbar({
      message,
      type: 'warning',
      timeout,
    });
  };

  /**
   * Show an info snackbar
   * @param message - Info message
   * @param timeout - Duration in ms (default: 3000, 0 for persistent)
   * @returns Snackbar ID
   */
  const showInfo = (message: string, timeout: number = 3000): number => {
    return showSnackbar({
      message,
      type: 'info',
      timeout,
    });
  };

  /**
   * Remove a specific snackbar by ID
   * @param id - Snackbar ID
   */
  const removeSnackbar = (id: number): void => {
    removeSnack(id);
  };

  return {
    showSnackbar,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    removeSnackbar,
    snacks: readonly(snacks),
    removeSnack,
  };
}

/**
 * Composable for managing stacking snackbars
 * Returns the same singleton instance every time
 * Provides methods to show success, error, warning, and info messages
 * Each snackbar appears independently and fades after its own timeout
 */
export function useSnackbar() {
  if (!snackbarInstance.value) {
    snackbarInstance.value = createSnackbarInstance();
  }
  return snackbarInstance.value;
}
